import React, { useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { DayPicker } from "react-day-picker";
import Modal from "../../../../../components/modal";

const ModalDateText = styled.p`
  color: #676767;
  font-size: 0.95rem;
  margin: 0 0 16px 0;
  text-align: center;
`;

const DayPickerContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const EndDateCalculator = styled.div`
  background: #f6f8fb;
  border: 1px solid #dde3ed;
  border-radius: 8px;
  margin-top: 16px;
  padding: 14px;
  text-align: left;
`;

const EndDateCalculatorTitle = styled.p`
  color: #181818;
  font-family: ${(props) => props.theme.font.primary};
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0 0 10px 0;
`;

const EndDateCalculatorLabel = styled.label`
  color: #676767;
  display: flex;
  flex-direction: column;
  font-family: ${(props) => props.theme.font.primary};
  font-size: 0.8rem;
  font-weight: 700;
  margin-bottom: 10px;

  input {
    background: #fff;
    border: 1px solid #d9dee8;
    border-radius: 6px;
    color: #181818;
    font-family: ${(props) => props.theme.font.secondary};
    font-size: 0.9rem;
    margin-top: 4px;
    padding: 8px 10px;
  }
`;

const EndDateCalculatorResult = styled.p`
  color: #13517a;
  font-size: 0.9rem;
  font-weight: 700;
  margin: 0;
`;

const EndDateCalculatorResultRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;

  @media(max-width: 520px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const EndDateCalculatorButton = styled.button`
  background: #070c29;
  border: none;
  border-radius: 6px;
  color: #fff;
  cursor: pointer;
  font-family: ${(props) => props.theme.font.primary};
  font-size: 0.85rem;
  font-weight: 700;
  padding: 8px 14px;
`;

function CoachingEndDateModal({
  clientName,
  isLoading,
  onClose,
  onSelectDay,
  onSubmit,
  open,
  selectedDay,
}) {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [calculatorStartDate, setCalculatorStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [calculatorDays, setCalculatorDays] = useState(90);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedMonth(selectedDay || new Date());
    setCalculatorStartDate(moment().format("YYYY-MM-DD"));
    setCalculatorDays(90);
  }, [open, selectedDay]);

  const getCalculatedEndDate = () => {
    const startDate = moment.utc(calculatorStartDate, "YYYY-MM-DD", true);
    const days = Number(calculatorDays);

    if (!startDate.isValid() || Number.isNaN(days)) {
      return null;
    }

    return startDate.add(days, "days");
  };

  const useCalculatedEndDate = () => {
    const calculatedEndDate = getCalculatedEndDate();
    if (calculatedEndDate) {
      const calculatedDate = calculatedEndDate.toDate();
      onSelectDay(calculatedDate);
      setSelectedMonth(calculatedDate);
    }
  };

  const calculatedEndDate = getCalculatedEndDate();

  return (
    <Modal
      disabled={!selectedDay || isLoading}
      handleSubmit={onSubmit}
      isLoading={isLoading}
      onClose={onClose}
      open={open}
      submitLabel="Save"
      title="Edit coaching plan end date"
    >
      <ModalDateText>
        {clientName ? `Select the actual end date for ${clientName}.` : ""}
      </ModalDateText>
      <DayPickerContainer>
        <DayPicker
          captionLayout="dropdown"
          fromYear={2024}
          month={selectedMonth}
          modifiersStyles={{ selected: { backgroundColor: "#1fc44a" } }}
          onDayClick={onSelectDay}
          onMonthChange={setSelectedMonth}
          selected={selectedDay}
          toYear={2035}
        />
      </DayPickerContainer>
      <EndDateCalculator>
        <EndDateCalculatorTitle>Calculate with start date</EndDateCalculatorTitle>
        <EndDateCalculatorLabel>
          Start date
          <input
            onChange={(e) => setCalculatorStartDate(e.target.value)}
            type="date"
            value={calculatorStartDate}
          />
        </EndDateCalculatorLabel>
        <EndDateCalculatorLabel>
          Days
          <input
            min="1"
            onChange={(e) => setCalculatorDays(e.target.value)}
            type="number"
            value={calculatorDays}
          />
        </EndDateCalculatorLabel>
        <EndDateCalculatorResultRow>
          <EndDateCalculatorResult>
            End date: {calculatedEndDate ? calculatedEndDate.format("MMMM D, YYYY") : "Select a valid start date and number of days"}
          </EndDateCalculatorResult>
          <EndDateCalculatorButton onClick={useCalculatedEndDate} type="button">Use this date</EndDateCalculatorButton>
        </EndDateCalculatorResultRow>
      </EndDateCalculator>
    </Modal>
  );
}

export default CoachingEndDateModal;
